import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Card, Row, Col, Form, Button, Table, Spinner, Modal,
} from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';

import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import AuthStorage from '~/Utils/AuthStorage';
import dialog from '~/Utils/dialog';
import {
  CARACTERISTICA_HOMOLOGADOR,
  CARACTERISTICA_IMPORTACAO_XLS_CONTAS,
} from '~/constant';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  processarPlanilhaContas,
  gravarImportacaoContas,
  listarEmpreendimentosImportacaoContas,
  listarGruposEmpresaImportacaoContas,
} from '~/Services/Service';
import { FaSearch } from 'react-icons/fa';

const SortIcon = ({ campo, sort }) => {
  if (sort.campo !== campo) return <span style={{ color: '#ccc' }}> ↕</span>;
  return <span> {sort.direcao === 'asc' ? '↑' : '↓'}</span>;
};

const extrairMensagemErro = (err) => {
  if (err && err.message) return err.message;
  return 'Erro ao processar a operação.';
};

const ImportacaoContas = () => {
  const [gesDisponiveis, setGesDisponiveis] = useState([]);
  const [geSelecionado, setGeSelecionado] = useState(null);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [empSelecionado, setEmpSelecionado] = useState(null);
  const [itensPreview, setItensPreview] = useState([]);
  const [loadingGes, setLoadingGes] = useState(true);
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [loadingGravar, setLoadingGravar] = useState(false);
  const [linhaDetalhe, setLinhaDetalhe] = useState(null);
  const [sort, setSort] = useState({ campo: 'numero_linha', direcao: 'asc' });
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Mount: carregar grupos de empresa do usuário (SetaGE — MAPEAMENTO_GLOBAIS.md)
  useEffect(() => {
    listarGruposEmpresaImportacaoContas()
      .then((res) => {
        const opcoes = (res || []).map((ge) => ({
          value: ge.codigo_grupo_empresa,
          label: `${ge.codigo_grupo_empresa} - ${ge.descricao}`,
        }));
        setGesDisponiveis(opcoes);
        if (opcoes.length === 1) {
          setGeSelecionado(opcoes[0]);
        }
      })
      .catch((err) => {
        toast.error(`Erro ao carregar grupos de empresa: ${extrairMensagemErro(err)}`);
      })
      .finally(() => setLoadingGes(false));
  }, []);

  // Cascata: ao trocar GE, recarregar empreendimentos (Ciclo 9 W-0510 L3 anti-race)
  useEffect(() => {
    if (!geSelecionado) {
      setEmpreendimentos([]);
      setEmpSelecionado(null);
      return;
    }
    let ativo = true;
    setLoadingEmps(true);
    setEmpSelecionado(null);
    listarEmpreendimentosImportacaoContas(geSelecionado.value)
      .then((res) => {
        if (!ativo) return;
        setEmpreendimentos(
          (res || []).map((e) => ({
            value: e.codigo_empreendimento,
            label: `${e.codigo_empreendimento} - ${e.descricao}`,
          })),
        );
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(`Erro ao carregar empreendimentos: ${extrairMensagemErro(err)}`);
      })
      .finally(() => {
        if (ativo) setLoadingEmps(false);
      });
    return () => { ativo = false; };
  }, [geSelecionado]);

  const handleChangeGe = useCallback(async (opt) => {
    if (itensPreview.length > 0) {
      const result = await dialog.confirm({
        message: 'Trocar o Grupo de Empresa descartará a importação atual. Continuar?',
        confirmLabel: 'Continuar',
        cancelLabel: 'Cancelar',
      });
      if (!result.proceed) return;
      setItensPreview([]);
      setExpandedRows(new Set());
      const fileInput = document.getElementById('file-upload-contas');
      if (fileInput) fileInput.value = '';
    }
    setGeSelecionado(opt);
  }, [itensPreview]);

  const handleUpload = useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Apenas arquivos .xlsx são aceitos.');
      const fi = document.getElementById('file-upload-contas');
      if (fi) fi.value = '';
      return;
    }

    if (!geSelecionado) {
      toast.error('Selecione o Grupo de Empresa antes de enviar o arquivo.');
      const fi = document.getElementById('file-upload-contas');
      if (fi) fi.value = '';
      return;
    }

    if (!empSelecionado) {
      toast.error('Selecione o Empreendimento antes de enviar o arquivo.');
      const fi = document.getElementById('file-upload-contas');
      if (fi) fi.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('codigo_grupo_empresa', geSelecionado.value);
    formData.append('codigo_empreendimento', empSelecionado.value);

    setLoadingUpload(true);
    setItensPreview([]);
    setExpandedRows(new Set());

    processarPlanilhaContas(formData)
      .then((res) => {
        const itens = res && res.itens ? res.itens : [];
        if (itens.length === 0) {
          toast.warn('Arquivo sem linhas válidas para importar.');
        } else {
          const totalErros = itens.filter((i) => i.criticas && i.criticas.length > 0).length;
          if (totalErros > 0) {
            toast.warn(`${itens.length} linha(s) lida(s). ${totalErros} com erro. Corrija e tente novamente.`);
          } else {
            toast.success(`${itens.length} linha(s) lida(s) sem erros. Pronto para importar.`);
          }
        }
        setItensPreview(itens);
      })
      .catch((err) => {
        toast.error(`Erro ao processar arquivo: ${extrairMensagemErro(err)}`);
        setItensPreview([]);
        const fi = document.getElementById('file-upload-contas');
        if (fi) fi.value = '';
      })
      .finally(() => setLoadingUpload(false));
  }, [geSelecionado, empSelecionado]);

  const handleGravar = useCallback(async () => {
    if (!geSelecionado) { toast.error('Selecione o Grupo de Empresa.'); return; }
    if (!empSelecionado) { toast.error('Selecione o Empreendimento.'); return; }
    if (!itensPreview || itensPreview.length === 0) {
      toast.error('Realize o upload da planilha antes de gravar.');
      return;
    }

    const result = await dialog.confirm({
      message: `Confirma a importação de ${itensPreview.length} conta(s)?`,
      confirmLabel: 'Importar',
      cancelLabel: 'Cancelar',
    });
    if (!result.proceed) return;

    const body = {
      codigo_grupo_empresa: geSelecionado.value,
      codigo_empreendimento: empSelecionado.value,
      itens: itensPreview.map((i) => i.dados),
    };

    setLoadingGravar(true);
    gravarImportacaoContas(body)
      .then((res) => {
        const qtd = res && res.quantidade_inseridas != null ? res.quantidade_inseridas : 0;
        const codigos = res && res.codigos_conta_gerados && res.codigos_conta_gerados.length > 0
          ? ` Código(s): ${res.codigos_conta_gerados.join(', ')}`
          : '';
        toast.success(`${qtd} conta(s) importada(s) com sucesso!${codigos}`);
        setItensPreview([]);
        setExpandedRows(new Set());
        const fi = document.getElementById('file-upload-contas');
        if (fi) fi.value = '';
      })
      .catch((err) => {
        toast.error(`Erro ao gravar importação: ${extrairMensagemErro(err)}`);
      })
      .finally(() => setLoadingGravar(false));
  }, [geSelecionado, empSelecionado, itensPreview]);

  const handleLimpar = useCallback(() => {
    setItensPreview([]);
    setExpandedRows(new Set());
    const fi = document.getElementById('file-upload-contas');
    if (fi) fi.value = '';
  }, []);

  const handleSort = useCallback((campo) => {
    setSort((prev) => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const toggleRow = useCallback((linha) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(linha)) next.delete(linha);
      else next.add(linha);
      return next;
    });
  }, []);

  const sortedItens = [...itensPreview].sort((a, b) => {
    const dir = sort.direcao === 'asc' ? 1 : -1;
    const dadosA = a.dados || {};
    const dadosB = b.dados || {};
    switch (sort.campo) {
      case 'numero_linha':
        return (a.numero_linha - b.numero_linha) * dir;
      case 'apelido_conta':
        return (dadosA.apelido_conta || '').localeCompare(dadosB.apelido_conta || '') * dir;
      case 'razao_social':
        return (dadosA.razao_social || '').localeCompare(dadosB.razao_social || '') * dir;
      case 'cpf':
        return (dadosA.cpf || '').localeCompare(dadosB.cpf || '') * dir;
      case 'tipo_pessoa':
        return (dadosA.tipo_pessoa || '').localeCompare(dadosB.tipo_pessoa || '') * dir;
      case 'estado_civil':
        return (dadosA.estado_civil || '').localeCompare(dadosB.estado_civil || '') * dir;
      case 'status': {
        const aErro = a.criticas && a.criticas.length > 0 ? 1 : 0;
        const bErro = b.criticas && b.criticas.length > 0 ? 1 : 0;
        return (aErro - bErro) * dir;
      }
      default:
        return 0;
    }
  });

  const canGravar = itensPreview.length > 0
    && !itensPreview.some((i) => i.criticas && i.criticas.length > 0);

  if (loadingGes) return <Loading />;

  if (
    !AuthStorage.temCaracteristica(CARACTERISTICA_HOMOLOGADOR) ||
    !AuthStorage.temCaracteristica(CARACTERISTICA_IMPORTACAO_XLS_CONTAS)
  ) {
    return (
      <Container fluid className="px-3 py-3">
        <Header title="Importação XLS de Contas" />
        <p className="text-muted">Você não possui permissão para acessar esta funcionalidade.</p>
      </Container>
    );
  }

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Importação XLS de Contas" />

      {/* Parâmetros */}
      <Card className="shadow-sm mb-3">
        <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff' }}>
          <strong>Parâmetros da Importação</strong>
        </Card.Header>
        <Card.Body className="py-2">
          <Row>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>
                  Grupo de Empresa <span className="text-danger">*</span>
                </Form.Label>
                <Select
                  value={geSelecionado}
                  onChange={handleChangeGe}
                  options={gesDisponiveis}
                  placeholder="Selecione..."
                  styles={customStyles}
                  components={{ DropdownIndicator }}
                  isClearable
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>
                  Empreendimento <span className="text-danger">*</span>
                </Form.Label>
                <Select
                  value={empSelecionado}
                  onChange={(opt) => setEmpSelecionado(opt)}
                  options={empreendimentos}
                  placeholder={
                    geSelecionado
                      ? loadingEmps ? 'Carregando...' : 'Selecione...'
                      : 'Selecione um GE primeiro'
                  }
                  isDisabled={!geSelecionado || loadingEmps}
                  isLoading={loadingEmps}
                  styles={customStyles}
                  components={{ DropdownIndicator }}
                  isClearable
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Upload */}
      <Card className="shadow-sm mb-3">
        <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff' }}>
          <strong>Upload de Planilha</strong>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} md={8}>
              <Form.Group>
                <Form.Label>
                  Selecione o arquivo XLSX <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  id="file-upload-contas"
                  type="file"
                  accept=".xlsx"
                  onChange={handleUpload}
                  disabled={loadingUpload || !geSelecionado || !empSelecionado}
                />
                {loadingUpload && (
                  <div className="mt-2">
                    <Spinner animation="border" size="sm" className="mr-2" />
                    <span>Processando arquivo...</span>
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} md={4} className="d-flex align-items-end pb-2">
              <Button
                variant="link"
                href="/templates/importacao-contas-template.xlsx"
                download
              >
                Baixar template XLSX
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Preview */}
      {itensPreview.length > 0 && (
        <Card className="shadow-sm mb-3">
          <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff' }}>
            <strong>
              {`Preview da Importação (${itensPreview.length} registro(s)${
                itensPreview.some((i) => i.criticas && i.criticas.length > 0)
                  ? ` — ${itensPreview.filter((i) => i.criticas && i.criticas.length > 0).length} com erro`
                  : ''
              })`}
            </strong>
          </Card.Header>
          <Card.Body className="p-0">
            <div style={{ overflowX: 'auto' }}>
              <Table striped bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onClick={() => handleSort('numero_linha')}
                    >
                      Linha<SortIcon campo="numero_linha" sort={sort} />
                    </th>
                    <th
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onClick={() => handleSort('apelido_conta')}
                    >
                      Apelido<SortIcon campo="apelido_conta" sort={sort} />
                    </th>
                    <th
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onClick={() => handleSort('razao_social')}
                    >
                      Razão Social<SortIcon campo="razao_social" sort={sort} />
                    </th>
                    <th
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onClick={() => handleSort('cpf')}
                    >
                      CPF<SortIcon campo="cpf" sort={sort} />
                    </th>
                    <th
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onClick={() => handleSort('tipo_pessoa')}
                    >
                      Tipo Pessoa<SortIcon campo="tipo_pessoa" sort={sort} />
                    </th>
                    <th
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onClick={() => handleSort('estado_civil')}
                    >
                      Estado Civil<SortIcon campo="estado_civil" sort={sort} />
                    </th>
                    <th style={{ whiteSpace: 'nowrap' }}>Cidade</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Email</th>
                    <th
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onClick={() => handleSort('status')}
                    >
                      Status<SortIcon campo="status" sort={sort} />
                    </th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItens.map((item) => {
                    const dados = item.dados || {};
                    const temErro = item.criticas && item.criticas.length > 0;
                    const conjuges = dados.conjuges_socios || [];
                    const temConjuge = conjuges.length > 0;
                    const expanded = expandedRows.has(item.numero_linha);
                    return (
                      <React.Fragment key={item.numero_linha}>
                        <tr style={temErro ? { backgroundColor: '#fff3cd' } : {}}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {temConjuge && (
                              <Button
                                variant="link"
                                className="p-0 mr-1"
                                onClick={() => toggleRow(item.numero_linha)}
                                title={expanded ? 'Recolher cônjuge/sócio' : 'Expandir cônjuge/sócio'}
                              >
                                {expanded ? '▼' : '▶'}
                              </Button>
                            )}
                            {item.numero_linha}
                          </td>
                          <td>{dados.apelido_conta}</td>
                          <td>{dados.razao_social}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{dados.cpf}</td>
                          <td>{dados.tipo_pessoa === '1' ? 'Jurídica' : 'Física'}</td>
                          <td>{dados.estado_civil}</td>
                          <td>{dados.cidade}</td>
                          <td>{dados.email}</td>
                          <td>
                            <span className={`badge badge-${temErro ? 'danger' : 'success'}`}>
                              {temErro ? 'ERRO' : 'OK'}
                            </span>
                          </td>
                          <td>
                            <Button
                              variant={temErro ? 'danger' : 'outline-secondary'}
                              onClick={() => setLinhaDetalhe(item)}
                            >
                              Detalhes
                            </Button>
                          </td>
                        </tr>
                        {expanded && conjuges.map((cs) => (
                          <tr
                            key={`cs-${item.numero_linha}-${cs.cpf || ''}-${cs.nome || ''}`}
                            style={{ backgroundColor: '#f8f9fa' }}
                          >
                            <td className="pl-4">
                              <small className="text-muted">↳ Cônjuge/Sócio</small>
                            </td>
                            <td colSpan={2}>{cs.nome}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>{cs.cpf}</td>
                            <td>{cs.tipo}</td>
                            <td>{cs.profissao}</td>
                            <td />
                            <td />
                            <td />
                            <td />
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Ações */}
      <div className="text-right mt-3">
        <Button
          variant="secondary"
          className="mr-2"
          onClick={handleLimpar}
          disabled={loadingUpload || loadingGravar}
        >
          Limpar
        </Button>
        <Button
          variant="primary"
          onClick={handleGravar}
          disabled={loadingUpload || loadingGravar || !canGravar}
          title={!canGravar && itensPreview.length > 0 ? 'Corrija os erros antes de importar' : ''}
        >
          {loadingGravar ? <Spinner animation="border" size="sm" /> : 'Gravar'}
        </Button>
      </div>

      {/* Modal Detalhes */}
      {linhaDetalhe && (
        <Modal show onHide={() => setLinhaDetalhe(null)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {`Detalhes — Linha ${linhaDetalhe.numero_linha}`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h6>
              {`${(linhaDetalhe.dados || {}).razao_social || '—'} (${(linhaDetalhe.dados || {}).apelido_conta || '—'})`}
            </h6>

            {linhaDetalhe.criticas && linhaDetalhe.criticas.length > 0 ? (
              <>
                <p className="text-danger mb-1">
                  <strong>{`Críticas (${linhaDetalhe.criticas.length}):`}</strong>
                </p>
                <ul className="mb-3">
                  {linhaDetalhe.criticas.map((c, ci) => (
                    <li key={`critica-${ci}`} className="text-danger">
                      {c.mensagem || (typeof c === 'string' ? c : JSON.stringify(c))}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-success mb-3">Linha sem críticas — pronta para importar.</p>
            )}

            <h6>Dados do Cliente</h6>
            <Table size="sm" bordered>
              <tbody>
                {[
                  ['CPF', (linhaDetalhe.dados || {}).cpf],
                  ['RG', (linhaDetalhe.dados || {}).rg],
                  ['Tipo Pessoa', (linhaDetalhe.dados || {}).tipo_pessoa],
                  ['Estado Civil', (linhaDetalhe.dados || {}).estado_civil],
                  ['Sexo', (linhaDetalhe.dados || {}).sexo],
                  ['Endereço', (linhaDetalhe.dados || {}).endereco],
                  ['Bairro', (linhaDetalhe.dados || {}).bairro],
                  ['CEP', (linhaDetalhe.dados || {}).cep],
                  ['Cidade', (linhaDetalhe.dados || {}).cidade],
                  ['Fone 1', (linhaDetalhe.dados || {}).fone1],
                  ['Fone 2', (linhaDetalhe.dados || {}).fone2],
                  ['Celular', (linhaDetalhe.dados || {}).celular],
                  ['Email', (linhaDetalhe.dados || {}).email],
                  ['Naturalidade', (linhaDetalhe.dados || {}).naturalidade],
                  ['Ramo', (linhaDetalhe.dados || {}).ramo],
                ].map(([label, value]) => (
                  <tr key={label}>
                    <td><strong>{label}</strong></td>
                    <td>{value || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {(linhaDetalhe.dados || {}).conjuges_socios
              && linhaDetalhe.dados.conjuges_socios.length > 0
              && (
                <>
                  <h6 className="mt-3">Cônjuge/Sócio</h6>
                  {linhaDetalhe.dados.conjuges_socios.map((cs, idx) => (
                    <div key={`modal-cs-${cs.cpf || ''}-${idx}`} className="mb-2">
                      <Table size="sm" bordered>
                        <tbody>
                          {[
                            ['Tipo', cs.tipo],
                            ['Nome', cs.nome],
                            ['CPF', cs.cpf],
                            ['RG', cs.rg],
                            ['Profissão', cs.profissao],
                          ].map(([label, value]) => (
                            <tr key={label}>
                              <td><strong>{label}</strong></td>
                              <td>{value || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ))}
                </>
              )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setLinhaDetalhe(null)}>Fechar</Button>
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  );
};

export default ImportacaoContas;
